import React from 'react';
import { PageBanner } from '../components/PageBanner';
import { ContactCTA } from '../components/ContactCTA';
import { useScrollAnimation } from '../hooks/useScrollAnimation';
import { MapPinIcon, ClockIcon, PhoneIcon, MailIcon, UserIcon, SendIcon } from 'lucide-react';
export function ContactPage() {
  const {
    ref: formRef,
    isVisible: isFormVisible
  } = useScrollAnimation();
  const {
    ref: infoRef,
    isVisible: isInfoVisible
  } = useScrollAnimation();
  return <main data-id="element-481">
      <PageBanner title="Contact Us" subtitle="We're here to help. Reach out to us anytime to discuss your care needs." breadcrumbs={[{
      label: 'Contact',
      path: '/contact'
    }]} data-id="element-482" />

      <ContactCTA data-id="element-483" />

      {/* Main Contact Section */}
      <section className="py-20 bg-white" data-id="element-484">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-485">
          <div className="grid grid-cols-1 lg:grid-cols-5 gap-12 lg:gap-16" data-id="element-486">
            {/* Contact Form — takes 3 columns */}
            <div ref={formRef} className={`lg:col-span-3 transition-all duration-1000 ease-out ${isFormVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-12'}`} data-id="element-487">
              <div className="bg-light-alt rounded-2xl p-8 md:p-10 border border-gray-100 shadow-card" data-id="element-488">
                <h3 className="text-2xl font-bold text-dark mb-2" data-id="element-489">
                  Send Us a Message
                </h3>
                <p className="text-dark-muted mb-8" data-id="element-490">
                  Fill out the form below and we'll get back to you as soon as
                  possible.
                </p>

                <form className="space-y-5" onSubmit={e => e.preventDefault()} data-id="element-491">
                  <div data-id="element-492">
                    <label htmlFor="name" className="block text-sm font-semibold text-dark mb-1.5" data-id="element-493">
                      Your Name <span className="text-red-500" data-id="element-494">*</span>
                    </label>
                    <input type="text" id="name" required className="w-full px-4 py-3 rounded-xl border border-gray-200 bg-white focus:ring-2 focus:ring-primary focus:border-primary outline-none transition-all text-dark placeholder-gray-400" placeholder="Enter your full name" data-id="element-495" />
                  </div>

                  <div data-id="element-496">
                    <label htmlFor="email" className="block text-sm font-semibold text-dark mb-1.5" data-id="element-497">
                      Your Email <span className="text-red-500" data-id="element-498">*</span>
                    </label>
                    <input type="email" id="email" required className="w-full px-4 py-3 rounded-xl border border-gray-200 bg-white focus:ring-2 focus:ring-primary focus:border-primary outline-none transition-all text-dark placeholder-gray-400" placeholder="Enter your email address" data-id="element-499" />
                  </div>

                  <div data-id="element-500">
                    <label htmlFor="subject" className="block text-sm font-semibold text-dark mb-1.5" data-id="element-501">
                      Subject
                    </label>
                    <input type="text" id="subject" className="w-full px-4 py-3 rounded-xl border border-gray-200 bg-white focus:ring-2 focus:ring-primary focus:border-primary outline-none transition-all text-dark placeholder-gray-400" placeholder="What is this regarding?" data-id="element-502" />
                  </div>

                  <div data-id="element-503">
                    <label htmlFor="message" className="block text-sm font-semibold text-dark mb-1.5" data-id="element-504">
                      Your Message
                    </label>
                    <textarea id="message" rows={6} className="w-full px-4 py-3 rounded-xl border border-gray-200 bg-white focus:ring-2 focus:ring-primary focus:border-primary outline-none transition-all resize-none text-dark placeholder-gray-400" placeholder="Tell us how we can help you..." data-id="element-505"></textarea>
                  </div>

                  <button type="submit" className="bg-primary hover:bg-primary-dark text-white font-bold py-3.5 px-8 rounded-xl transition-all shadow-md hover:shadow-lg flex items-center justify-center group" data-id="element-506">
                    <SendIcon className="mr-2 h-5 w-5 group-hover:translate-x-0.5 transition-transform" data-id="element-507" />
                    Send Message
                  </button>
                </form>
              </div>
            </div>

            {/* Contact Info Sidebar — takes 2 columns */}
            <div ref={infoRef} className={`lg:col-span-2 transition-all duration-1000 delay-200 ease-out ${isInfoVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-12'}`} data-id="element-508">
              <div className="space-y-8" data-id="element-509">
                {/* Director Info */}
                <div className="bg-primary-dark text-white rounded-2xl p-8 shadow-xl" data-id="element-510">
                  <div className="flex items-center mb-6" data-id="element-511">
                    <div className="bg-white/10 p-3 rounded-xl mr-4" data-id="element-512">
                      <UserIcon className="h-7 w-7 text-secondary" data-id="element-513" />
                    </div>
                    <div data-id="element-514">
                      <h4 className="font-bold text-lg" data-id="element-515">
                        Ijeoma Nwokorie Igwe
                      </h4>
                      <p className="text-white/70 text-sm font-medium" data-id="element-516">
                        Director
                      </p>
                    </div>
                  </div>
                  <div className="space-y-4 text-white/90" data-id="element-517">
                    <div className="flex items-start" data-id="element-518">
                      <MapPinIcon className="h-5 w-5 text-secondary mr-3 mt-0.5 flex-shrink-0" data-id="element-519" />
                      <div data-id="element-520">
                        <p className="font-semibold text-white" data-id="element-521">Address</p>
                        <p className="text-white/80 text-sm" data-id="element-522">
                          Indian Head
                          <br data-id="element-523" />
                          Maryland, 20640
                        </p>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Phone Numbers */}
                <div className="bg-light-alt rounded-2xl p-8 border border-gray-100 shadow-sm" data-id="element-524">
                  <h4 className="font-bold text-dark text-lg mb-5 flex items-center" data-id="element-525">
                    <PhoneIcon className="h-5 w-5 text-primary mr-2" data-id="element-526" />
                    Phone Numbers
                  </h4>
                  <div className="space-y-4" data-id="element-527">
                    <a href="tel:2405358731" className="flex items-center justify-between p-3 rounded-xl hover:bg-white hover:shadow-sm transition-all group" data-id="element-528">
                      <div data-id="element-529">
                        <p className="text-xs font-semibold text-dark-muted uppercase tracking-wider" data-id="element-530">
                          Phone
                        </p>
                        <p className="text-dark font-bold group-hover:text-primary transition-colors" data-id="element-531">
                          (240) 535-8731
                        </p>
                      </div>
                      <PhoneIcon className="h-4 w-4 text-gray-300 group-hover:text-primary transition-colors" data-id="element-532" />
                    </a>
                    <a href="tel:8005790970" className="flex items-center justify-between p-3 rounded-xl hover:bg-white hover:shadow-sm transition-all group" data-id="element-533">
                      <div data-id="element-534">
                        <p className="text-xs font-semibold text-dark-muted uppercase tracking-wider" data-id="element-535">
                          Office
                        </p>
                        <p className="text-dark font-bold group-hover:text-primary transition-colors" data-id="element-536">
                          (800) 579-0970
                        </p>
                      </div>
                      <PhoneIcon className="h-4 w-4 text-gray-300 group-hover:text-primary transition-colors" data-id="element-537" />
                    </a>
                    <a href="tel:3015864767" className="flex items-center justify-between p-3 rounded-xl hover:bg-white hover:shadow-sm transition-all group" data-id="element-538">
                      <div data-id="element-539">
                        <p className="text-xs font-semibold text-dark-muted uppercase tracking-wider" data-id="element-540">
                          Office 2
                        </p>
                        <p className="text-dark font-bold group-hover:text-primary transition-colors" data-id="element-541">
                          (301) 586-4767
                        </p>
                      </div>
                      <PhoneIcon className="h-4 w-4 text-gray-300 group-hover:text-primary transition-colors" data-id="element-542" />
                    </a>
                  </div>
                </div>

                {/* Email */}
                <div className="bg-light-alt rounded-2xl p-8 border border-gray-100 shadow-sm" data-id="element-543">
                  <h4 className="font-bold text-dark text-lg mb-4 flex items-center" data-id="element-544">
                    <MailIcon className="h-5 w-5 text-primary mr-2" data-id="element-545" />
                    Email
                  </h4>
                  <a href="mailto:info@edvinhomehealth.com" className="text-primary font-semibold hover:text-primary-dark hover:underline transition-colors" data-id="element-546">
                    info@edvinhomehealth.com
                  </a>
                </div>

                {/* Business Hours */}
                <div className="bg-light-alt rounded-2xl p-8 border border-gray-100 shadow-sm" data-id="element-547">
                  <h4 className="font-bold text-dark text-lg mb-4 flex items-center" data-id="element-548">
                    <ClockIcon className="h-5 w-5 text-secondary mr-2" data-id="element-549" />
                    Business Hours
                  </h4>
                  <div className="space-y-2 text-dark-muted" data-id="element-550">
                    <div className="flex justify-between" data-id="element-551">
                      <span data-id="element-552">Monday – Friday</span>
                      <span className="font-semibold text-dark" data-id="element-553">
                        8:00 AM – 5:00 PM
                      </span>
                    </div>
                    <div className="flex justify-between" data-id="element-554">
                      <span data-id="element-555">Saturday</span>
                      <span className="font-semibold text-dark" data-id="element-556">
                        By Appointment
                      </span>
                    </div>
                    <div className="flex justify-between" data-id="element-557">
                      <span data-id="element-558">Sunday</span>
                      <span className="font-semibold text-dark" data-id="element-559">Closed</span>
                    </div>
                  </div>
                  <div className="mt-4 pt-4 border-t border-gray-200" data-id="element-560">
                    <p className="text-primary font-bold text-sm" data-id="element-561">
                      🟢 24/7 On-Call Nursing Support Available
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>
    </main>;
}